import {
  Button,
  Combobox,
  Dialog,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Dropdown,
  Field,
  Input,
  mergeClasses,
  Option,
  OptionGroup,
  Spinner,
  Textarea,
  Tooltip,
} from "@fluentui/react-components";
import {
  ArrowLeftRegular,
  ArrowRightRegular,
  CheckmarkRegular,
  DismissRegular,
} from "@fluentui/react-icons";
import { createUseCasePrompt, getCategories, updateUseCasePrompt } from "api";
import { ConstantValues } from "common/constants";
import { RichTextField } from "components/cells/RichTextFieldCell";
import { CustomToggle } from "components/customToggle/CustomToggle";
import { DialogFooterAction } from "components/dialogFooterAction/DialogFooterAction";
import { DialogTabs } from "components/dialogTabs/DialogTabs";
import { FieldError } from "components/fieldError/FieldError";
import { PeoplePicker } from "components/fields/peoplePicker/PeoplePicker";
import { CustomOption } from "components/fields/peoplePicker/PeoplePicker.types";
import { ColumnTypes } from "models/enums/ColumnTypes";
import { AccessType, IAccessLLMModel } from "models/IAccessLLMModel";
import { ICategory } from "models/ICategory";
import { ITag } from "models/ITag";
import { IUseCasePrompt } from "models/IUseCasePrompt";
import { IUser } from "models/IUser";
import { ChangeEvent, useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "store";
import {
  useSharedDialogContentStyles,
  useSharedDialogFooterStyles,
  useSharedDropdownStyles,
} from "theme/sharedStyles";
import {
  getModelByKey,
  isRichTextEmpty,
  mapAzureUsersToModel,
  normalizeModelKey,
} from "utils/generalUtils";
import {
  addUseCasePrompt,
  updateUseCasePromptInSlice,
} from "../../../redux/useCasePromptsSlice";
import {
  getKnowledgeComponentsLabel,
  getNextTab,
  getPrevTab,
} from "../useCasePromptUtils";
import { useStyles } from "./EditUseCasePrompt.styles";
import {
  ErrorMessages,
  IEditUseCasePromptProps,
  IMappedUser,
  TabValues,
} from "./EditUseCasePrompt.types";

const byOrderAsc = (a: IAccessLLMModel, b: IAccessLLMModel) =>
  (a.order ?? 0) - (b.order ?? 0);

export const EditUseCasePrompt = (
  props: IEditUseCasePromptProps,
): JSX.Element => {
  const styles = useStyles();
  const contentStyles = useSharedDialogContentStyles();
  const dropdownStyles = useSharedDropdownStyles();
  const footerStyles = useSharedDialogFooterStyles();
  const dispatch = useDispatch();
  const useCases = useSelector((state: RootState) =>
    state.useCasesSlice.value.filter(
      (uc) => uc.title !== ConstantValues.OTHER_USECASE_NAME,
    ),
  );
  const currentUser = useSelector(
    (state: RootState) => state.currentUserSlice.value,
  );
  const models: IAccessLLMModel[] = useSelector(
    (state: RootState) => state.accessLLMModelsSlice.value,
  );
  const currentChatConfig = useSelector(
    (state: RootState) =>
      state.currentLLMConfigSlice.config[ConstantValues.CHAT],
  );
  const users: IUser[] = useSelector(
    (state: RootState) => state.usersSlice.value,
  );

  const [title, setTitle] = useState(
    props.useCasePrompt?.title || ConstantValues.EMPTY_STRING,
  );
  const [prompt, setPrompt] = useState(
    props.useCasePrompt?.prompt || ConstantValues.EMPTY_STRING,
  );
  const [description, setDescription] = useState(
    props.useCasePrompt?.description || ConstantValues.EMPTY_STRING,
  );
  const [isQualsEnabled, setIsQualsEnabled] = useState<boolean>(
    props.useCasePrompt?.qualsEnabled ?? false,
  );
  const [isProposalsEnabled, setIsProposalsEnabled] = useState<boolean>(
    props.useCasePrompt?.proposalsEnabled ?? false,
  );
  const [areCitationsEnabled, setAreCitationsEnabled] = useState<boolean>(
    props.useCasePrompt?.citationsEnabled ?? false,
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [owner, setOwner] = useState<CustomOption | null>(
    props.useCasePrompt?.owner
      ? {
          id: props.useCasePrompt.owner.userId,
          text: props.useCasePrompt.owner.displayName,
          key: props.useCasePrompt.owner.userId,
        }
      : currentUser
        ? {
            id: currentUser.id,
            text: currentUser.displayName,
            key: currentUser.id,
          }
        : null,
  );
  const [selectedLlmKey, setSelectedLlmKey] = useState<string | undefined>(
    undefined,
  );
  const [preselectedKnowledgeComponents, setPreselectedKnowledgeComponents] =
    useState<string[]>(
      props.useCasePrompt?.preselectedKnowledgeComponents || [],
    );
  const [categories, setCategories] = useState<ICategory[]>([]);
  const [categoriesLoading, setCategoriesLoading] = useState<boolean>(true);
  const [selectedTab, setSelectedTab] = useState<string>(TabValues.DETAILS);

  useEffect(() => {
    if (selectedLlmKey) {
      return;
    }

    if (!currentChatConfig?.llmType) {
      return;
    }

    setSelectedLlmKey(normalizeModelKey(currentChatConfig.llmType));
  }, [selectedLlmKey, currentChatConfig?.llmType]);

  useEffect(() => {
    const existingKey = normalizeModelKey(props.useCasePrompt?.llmModel);
    if (!existingKey) return;

    const match = getModelByKey(models, existingKey);
    if (!match) return;

    setSelectedLlmKey(normalizeModelKey(match.name || match.id));
  }, [props.useCasePrompt?.llmModel, models]);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setCategoriesLoading(true);
        const fetchedCategories = await getCategories(true);
        setCategories(fetchedCategories);
      } catch (error) {
        console.error("Error fetching categories:", error);
      } finally {
        setCategoriesLoading(false);
      }
    };

    if (props.isOpen) {
      fetchCategories();
    }
  }, [props.isOpen]);

  let initialUseCaseIds: string[] = [];
  if (props.selectedUseCaseId) {
    initialUseCaseIds = [props.selectedUseCaseId];
  } else if (
    props.useCasePrompt?.useCaseIds &&
    props.useCasePrompt.useCaseIds.length > 0
  ) {
    initialUseCaseIds = props.useCasePrompt.useCaseIds;
  }

  const [selectedUseCaseIds, setSelectedUseCaseIds] =
    useState<string[]>(initialUseCaseIds);

  const handleSave = async (): Promise<void> => {
    const errors: Record<string, string> = {};

    if (!title.trim()) errors.title = "Title is required";
    if (!description.trim()) errors.description = "Description is required";
    if (!selectedUseCaseIds || selectedUseCaseIds.length === 0) {
      errors.useCase = "At least one Use Case is required";
    }
    if (isRichTextEmpty(prompt)) errors.prompt = "Prompt is required";
    if (!selectedLlmKey) errors.llmModel = "LLM Model is required";

    const selectedUseCases = useCases.filter((u) =>
      selectedUseCaseIds.includes(u.id),
    );

    if (
      selectedUseCaseIds &&
      selectedUseCaseIds.length > 0 &&
      selectedUseCases.length === 0
    ) {
      errors.useCase = ErrorMessages.USECASE_NOT_FOUND;
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    setFieldErrors({});

    setIsLoading(true);
    try {
      const now = Math.floor(Date.now() / 1000);

      const promptData: IUseCasePrompt = {
        id: props.useCasePrompt?.id ?? crypto.randomUUID(),
        title,
        prompt,
        description,
        qualsEnabled: isQualsEnabled,
        proposalsEnabled: isProposalsEnabled,
        citationsEnabled: areCitationsEnabled,
        createdAt: props.useCasePrompt?.createdAt ?? now,
        llmModel: normalizeModelKey(selectedLlmKey),
        preselectedKnowledgeComponents: preselectedKnowledgeComponents,
        updatedAt: now,
        user: props.useCasePrompt?.user ?? {
          userId: currentUser.id,
          displayName: currentUser.displayName,
        },
        ratings: props.useCasePrompt?.ratings || [],
        favoritedBy: props.useCasePrompt?.favoritedBy || [],
        useCaseIds: selectedUseCaseIds,
      };

      if (owner) {
        promptData.owner = {
          userId: owner.id!,
          displayName: owner.text!,
        };
      }

      if (props.useCasePrompt?.id) {
        const updatedPrompt = await updateUseCasePrompt({
          promptId: props.useCasePrompt.id,
          prompt: promptData,
        });
        dispatch(updateUseCasePromptInSlice(updatedPrompt));
      } else {
        const createdPrompt = await createUseCasePrompt({
          prompt: promptData,
        });
        dispatch(addUseCasePrompt(createdPrompt));
      }

      props.onDismiss(true);
    } catch (error) {
      console.error("Error saving use case prompt:", error);
      setFieldErrors({ general: ErrorMessages.SAVE_FAILED });
    } finally {
      setIsLoading(false);
    }
  };

  const handlePromptChange = (_: unknown, value: string | undefined): void => {
    setPrompt(value || ConstantValues.EMPTY_STRING);
    if (fieldErrors.prompt)
      setFieldErrors((prev) => ({
        ...prev,
        prompt: ConstantValues.EMPTY_STRING,
      }));
  };

  const selectedModel = models.find(
    (m) => normalizeModelKey(m.name || m.id) === selectedLlmKey,
  );

  const readResourcesForPickerFieldFiltered = useCallback(
    async (searchedValue: string): Promise<IMappedUser[]> => {
      return new Promise((resolve) => {
        const mappedUsers = mapAzureUsersToModel(users) as IMappedUser[];
        const filteredResources = mappedUsers.filter((resource: IMappedUser) =>
          resource.Name?.toLowerCase().includes(searchedValue.toLowerCase()),
        );

        resolve(filteredResources);
      });
    },
    [users],
  );

  const handleSelectedOwner = (selected: CustomOption[]): void => {
    if (selected.length === 0) {
      setOwner(null);
      return;
    }

    const newOwner: CustomOption | null =
      selected.find((o) => o !== owner) || null;
    setOwner(newOwner);
  };

  const selectedKnowledgeComponentsLabel = getKnowledgeComponentsLabel(
    preselectedKnowledgeComponents,
    categories,
  );

  const handleTabClick = (tab: string): void => {
    if (tab === selectedTab) return;
    if (!validateCurrentTab()) return;
    setSelectedTab(tab);
  };

  const validateCurrentTab = (): boolean => {
    const errors: Record<string, string> = { ...fieldErrors };
    let hasErrors = false;

    if (selectedTab === TabValues.DETAILS) {
      if (!title.trim()) {
        errors.title = "Title is required";
        hasErrors = true;
      }
      if (!description.trim()) {
        errors.description = "Description is required";
        hasErrors = true;
      }
      if (!selectedUseCaseIds || selectedUseCaseIds.length === 0) {
        errors.useCase = "At least one Use Case is required";
        hasErrors = true;
      }
    } else if (selectedTab === TabValues.PROMPT) {
      if (isRichTextEmpty(prompt)) {
        errors.prompt = "Prompt is required";
        hasErrors = true;
      }
    }

    if (hasErrors) {
      setFieldErrors(errors);
    }
    return !hasErrors;
  };

  const handleNextTab = () => {
    if (!validateCurrentTab()) return;
    const nextTab = getNextTab(selectedTab);
    if (nextTab) setSelectedTab(nextTab);
  };

  const handlePrevTab = () => {
    const prevTab = getPrevTab(selectedTab);
    if (prevTab) setSelectedTab(prevTab);
  };

  const getSystemDefaultLLM = () => {
    if (!currentChatConfig?.llmType) return null;
    const defaultKey = normalizeModelKey(currentChatConfig.llmType);
    return models.find((m) => normalizeModelKey(m.name || m.id) === defaultKey);
  };

  const getTabErrors = (tabValue: string): string[] => {
    switch (tabValue) {
      case TabValues.DETAILS:
        return Object.keys(fieldErrors).filter(
          (key) =>
            ["title", "description", "useCase"].includes(key) &&
            fieldErrors[key],
        );
      case TabValues.PROMPT:
        return Object.keys(fieldErrors).filter(
          (key) => key === "prompt" && fieldErrors[key],
        );
      case TabValues.CONFIGURATION:
        return Object.keys(fieldErrors).filter(
          (key) => key === "llmModel" && fieldErrors[key],
        );
      default:
        return [];
    }
  };

  const isFirstTab = selectedTab === TabValues.DETAILS;
  const isLastTab = selectedTab === TabValues.CONFIGURATION;
  const hasCurrentTabErrors = getTabErrors(selectedTab).length > 0;

  const isDetailsTabComplete =
    title.trim() !== ConstantValues.EMPTY_STRING &&
    description.trim() !== ConstantValues.EMPTY_STRING &&
    selectedUseCaseIds.length > 0;

  const isPromptTabComplete = !isRichTextEmpty(prompt);

  const isConfigurationTabComplete = !!selectedLlmKey;

  const isCurrentTabComplete =
    (selectedTab === TabValues.DETAILS && isDetailsTabComplete) ||
    (selectedTab === TabValues.PROMPT && isPromptTabComplete) ||
    (selectedTab === TabValues.CONFIGURATION && isConfigurationTabComplete);

  const isAllTabsComplete =
    isDetailsTabComplete && isPromptTabComplete && isConfigurationTabComplete;

  const systemDefaultModel = getSystemDefaultLLM();
  const publicModels = useMemo(
    () => models.filter((m) => m.accessType === AccessType.PUBLIC),
    [models],
  );
  const sortedModels = useMemo(() => {
    const defaultModel = systemDefaultModel;
    return [
      ...(defaultModel && defaultModel.accessType === AccessType.PUBLIC
        ? [defaultModel]
        : []),
      ...publicModels.filter(
        (m) =>
          !defaultModel ||
          normalizeModelKey(m.name || m.id) !==
            normalizeModelKey(defaultModel.name || defaultModel.id),
      ),
    ].sort(byOrderAsc);
  }, [systemDefaultModel, publicModels]);

  return (
    <Dialog
      open={props.isOpen}
      modalType="modal"
      onOpenChange={(_, data) => {
        if (!data.open && !isLoading) props.onDismiss(false);
      }}
    >
      <DialogSurface
        className={mergeClasses(
          styles.dialogSurface,
          footerStyles.dialogSurface,
        )}
      >
        <DialogBody>
          <DialogTitle
            action={
              <Button
                appearance="subtle"
                aria-label="Close dialog"
                icon={<DismissRegular />}
                onClick={() => props.onDismiss(false)}
                disabled={isLoading}
              />
            }
          >
            {props.useCasePrompt
              ? "Update Use Case Prompt"
              : "Add Use Case Prompt"}
          </DialogTitle>
          <DialogContent
            className={mergeClasses(
              contentStyles.tabbedDialogContent,
              styles.dialogContent,
            )}
          >
            <DialogTabs
              tabs={[
                { key: TabValues.DETAILS, label: "Details" },
                { key: TabValues.PROMPT, label: "Prompt" },
                { key: TabValues.CONFIGURATION, label: "Configuration" },
              ]}
              activeTab={selectedTab}
              onTabClick={handleTabClick}
            />
            <div
              className={mergeClasses(
                contentStyles.formContainer,
                contentStyles.scrollableContent,
              )}
            >
              {isLoading && (
                <div className={contentStyles.formLoadingOverlay}>
                  <Spinner label="Saving..." />
                </div>
              )}

              <div
                className={mergeClasses(
                  contentStyles.tabContent,
                  styles.tabContent,
                )}
              >
                {selectedTab === TabValues.DETAILS && (
                  <>
                    <Field
                      label="Title"
                      required
                      validationState={fieldErrors.title ? "error" : "none"}
                      validationMessage={fieldErrors.title}
                    >
                      <Input
                        value={title}
                        placeholder="Enter use case prompt title"
                        onChange={(e: ChangeEvent<HTMLInputElement>) => {
                          setTitle(e.target.value);
                          if (fieldErrors.title)
                            setFieldErrors((prev) => ({
                              ...prev,
                              title: ConstantValues.EMPTY_STRING,
                            }));
                        }}
                        disabled={isLoading}
                      />
                    </Field>
                    <Field
                      label="Description"
                      required
                      validationState={
                        fieldErrors.description ? "error" : "none"
                      }
                      validationMessage={fieldErrors.description}
                    >
                      <Textarea
                        value={description}
                        placeholder="Enter description"
                        resize="vertical"
                        onChange={(e: ChangeEvent<HTMLTextAreaElement>) => {
                          setDescription(e.target.value);
                          if (fieldErrors.description)
                            setFieldErrors((prev) => ({
                              ...prev,
                              description: ConstantValues.EMPTY_STRING,
                            }));
                        }}
                        disabled={isLoading}
                      />
                    </Field>
                    <Field
                      label="Select Use Cases"
                      required
                      validationState={fieldErrors.useCase ? "error" : "none"}
                      validationMessage={fieldErrors.useCase}
                    >
                      <Combobox
                        multiselect
                        placeholder="Select one or more use cases"
                        positioning="below"
                        className={dropdownStyles.dropdown}
                        listbox={{
                          className: dropdownStyles.listbox,
                        }}
                        selectedOptions={selectedUseCaseIds}
                        value={
                          selectedUseCaseIds
                            .map(
                              (id) => useCases.find((u) => u.id === id)?.title,
                            )
                            .filter(Boolean)
                            .join(", ") || ConstantValues.EMPTY_STRING
                        }
                        onOptionSelect={(_, data) => {
                          setSelectedUseCaseIds(data.selectedOptions);
                          if (fieldErrors.useCase)
                            setFieldErrors((prev) => ({
                              ...prev,
                              useCase: ConstantValues.EMPTY_STRING,
                            }));
                        }}
                        disabled={isLoading}
                      >
                        {[...useCases]
                          .sort((a, b) => a.title.localeCompare(b.title))
                          .map((useCase) => (
                            <Option
                              key={useCase.id}
                              value={useCase.id}
                              className={styles.comboboxOption}
                            >
                              {useCase.title}
                            </Option>
                          ))}
                      </Combobox>
                    </Field>
                    <Field label="Owner">
                      <PeoplePicker
                        id="user"
                        handleSelectedItems={handleSelectedOwner}
                        disabled={isLoading}
                        required={false}
                        hasOnlyOurUsers={true}
                        readResourcesForPickerFieldFiltered={
                          readResourcesForPickerFieldFiltered
                        }
                        selectedOptions={owner ? [owner] : []}
                      />
                    </Field>
                  </>
                )}

                {selectedTab === TabValues.PROMPT && (
                  <>
                    <Field label="Prompt" required />
                    <div className={styles.promptEditorWrapper}>
                      <RichTextField
                        value={prompt}
                        editable
                        onChange={handlePromptChange}
                        type={ColumnTypes.RichText}
                      />
                    </div>
                    {fieldErrors.prompt && (
                      <FieldError message={fieldErrors.prompt} />
                    )}
                  </>
                )}

                {selectedTab === TabValues.CONFIGURATION && (
                  <>
                    <CustomToggle
                      checked={isQualsEnabled}
                      onChange={(checked) => setIsQualsEnabled(checked)}
                      label="Enable Quals"
                      hint="Allow qualifications search in this prompt"
                      disabled={isLoading}
                    />
                    <CustomToggle
                      checked={isProposalsEnabled}
                      onChange={(checked) => setIsProposalsEnabled(checked)}
                      label="Enable Proposals"
                      hint="Allow proposals search in this prompt"
                      disabled={isLoading}
                    />
                    <CustomToggle
                      checked={areCitationsEnabled}
                      onChange={(checked) => setAreCitationsEnabled(checked)}
                      label="Enable Citations"
                      hint="Show citations in prompt responses"
                      disabled={isLoading}
                    />

                    <Field
                      label="LLM Model"
                      required
                      validationState={fieldErrors.llmModel ? "error" : "none"}
                      validationMessage={fieldErrors.llmModel}
                    >
                      <Dropdown
                        className={dropdownStyles.dropdown}
                        listbox={{
                          className: dropdownStyles.listbox,
                        }}
                        positioning="below"
                        value={
                          selectedModel?.displayName ??
                          ConstantValues.EMPTY_STRING
                        }
                        onOptionSelect={(_, data) => {
                          setSelectedLlmKey(
                            normalizeModelKey(data.optionValue as string),
                          );
                          if (fieldErrors.llmModel)
                            setFieldErrors((prev) => ({
                              ...prev,
                              llmModel: ConstantValues.EMPTY_STRING,
                            }));
                        }}
                        disabled={isLoading}
                      >
                        {sortedModels.map((m) => {
                          const value = normalizeModelKey(m.name || m.id);
                          const isDefault = m.id === systemDefaultModel?.id;
                          return (
                            <Option
                              key={m.id}
                              value={value}
                              className={styles.comboboxOption}
                            >
                              {isDefault
                                ? `${m.displayName ?? m.name} (System Default)`
                                : (m.displayName ?? m.name)}
                            </Option>
                          );
                        })}
                      </Dropdown>
                    </Field>

                    <Field label="Preselected Knowledge Components">
                      {categoriesLoading ? (
                        <Spinner size="tiny" label="Loading..." />
                      ) : (
                        <Tooltip
                          content={
                            selectedKnowledgeComponentsLabel ||
                            "Select knowledge components"
                          }
                          relationship="label"
                        >
                          <Combobox
                            multiselect
                            placeholder="Select knowledge components"
                            selectedOptions={preselectedKnowledgeComponents}
                            value={selectedKnowledgeComponentsLabel}
                            onChange={() => {}}
                            freeform={false}
                            disabled={categoriesLoading || isLoading}
                            positioning="after"
                            onOptionSelect={(_, data) => {
                              setPreselectedKnowledgeComponents(
                                data.selectedOptions,
                              );
                            }}
                            className={styles.knowledgeComponentCombobox}
                            listbox={{
                              className: styles.knowledgeComponentListbox,
                            }}
                          >
                            {categories.map((category: ICategory) => (
                              <OptionGroup
                                key={category.id}
                                label={category.title}
                              >
                                {category.tags.map((tag: ITag) => (
                                  <Option
                                    key={tag.id}
                                    value={tag.id}
                                    className={styles.comboboxOption}
                                  >
                                    {tag.name}
                                  </Option>
                                ))}
                              </OptionGroup>
                            ))}
                          </Combobox>
                        </Tooltip>
                      )}
                    </Field>
                  </>
                )}

                {fieldErrors.general && (
                  <FieldError message={fieldErrors.general} />
                )}
              </div>
            </div>
          </DialogContent>
        </DialogBody>
        {!isLoading && (
          <div className={footerStyles.dialogFooter}>
            <div className={styles.actionButtons}>
              <DialogFooterAction
                icon={<ArrowLeftRegular />}
                onClick={handlePrevTab}
                ariaHidden={isFirstTab}
                tabIndex={isFirstTab ? -1 : undefined}
                className={isFirstTab ? styles.navButtonHidden : undefined}
                tooltip="Previous"
              />
              <div className={styles.navButtonContainer}>
                <DialogFooterAction
                  icon={<ArrowRightRegular />}
                  onClick={handleNextTab}
                  dimmed={!isCurrentTabComplete}
                  ariaHidden={isLastTab}
                  tabIndex={isLastTab ? -1 : undefined}
                  className={mergeClasses(
                    styles.navButtonAbsolute,
                    isLastTab && styles.navButtonHidden,
                  )}
                  tooltip="Next"
                />
                <DialogFooterAction
                  icon={<CheckmarkRegular />}
                  onClick={handleSave}
                  disabled={isLoading || hasCurrentTabErrors}
                  dimmed={!isAllTabsComplete}
                  ariaHidden={!isLastTab}
                  tabIndex={!isLastTab ? -1 : undefined}
                  className={mergeClasses(
                    styles.navButtonAbsolute,
                    !isLastTab && styles.navButtonHidden,
                  )}
                  tooltip="Save"
                />
              </div>
            </div>
          </div>
        )}
      </DialogSurface>
    </Dialog>
  );
};
